import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Metric } from '../components/Metric'
import { PubBadge } from '../components/PubBadge'
import { api, type TitleDetail } from '../api/client'
import { fmtInt, fmtMoney, publicationColor } from '../lib/format'

function AgencyScheduleCell({
  count, revenuePrevailing, revenueRemit, bold,
}: {
  count: number
  revenuePrevailing: number
  revenueRemit: number
  bold?: boolean
}) {
  return (
    <td className="px-4 py-3 text-right border-l border-ink-500 whitespace-nowrap">
      <div
        className={`font-mono tabular-nums ${bold ? 'font-semibold' : ''} ${
          count === 0 ? 'text-paper-trace' : 'text-radar-red'
        }`}
      >
        {fmtInt(count)}
      </div>
      {count > 0 ? (
        <div className="font-mono text-[10px] tabular-nums mt-0.5">
          <div>
            <span className="text-paper-faint">list </span>
            <span className="text-radar-green/70">{fmtMoney(revenuePrevailing, false, 2)}</span>
          </div>
          <div>
            <span className="text-paper-faint">remit </span>
            <span className="text-radar-green">{fmtMoney(revenueRemit, false, 2)}</span>
          </div>
        </div>
      ) : (
        <div className="font-mono text-[11px] text-paper-trace">$0.00</div>
      )}
    </td>
  )
}

function RenewScheduleCell({
  count, revenue, tone, noBorder, bold,
}: {
  count: number
  revenue: number
  tone: 'amber' | 'cyan' | 'violet'
  noBorder?: boolean
  bold?: boolean
}) {
  const toneClass = {
    amber: 'text-amber-glow',
    cyan: 'text-radar-cyan',
    violet: 'text-radar-violet',
  }[tone]
  return (
    <td className={`px-4 py-3 text-right ${noBorder ? '' : 'border-l border-ink-500'}`}>
      <div
        className={`font-mono tabular-nums ${bold ? 'font-semibold' : ''} ${
          count === 0 ? 'text-paper-trace' : toneClass
        }`}
      >
        {fmtInt(count)}
      </div>
      <div
        className={`font-mono text-[11px] tabular-nums ${
          revenue === 0 ? 'text-paper-trace' : 'text-radar-green'
        }`}
      >
        {fmtMoney(revenue, false, 2)}
      </div>
    </td>
  )
}

function SplitMetric({
  total, totalTone, withEmail, noEmail,
}: {
  total: number
  totalTone: 'green' | 'cyan'
  withEmail: number
  noEmail: number
}) {
  const pct = (n: number) =>
    total > 0 ? `${Math.round((n / total) * 100)}%` : ''
  const toneClass = totalTone === 'green' ? 'text-radar-green' : 'text-radar-cyan'
  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-2">
        <span className={`font-mono font-semibold text-4xl tabular-nums ${toneClass}`}>
          {fmtInt(total)}
        </span>
        <span className="label text-paper-faint">active</span>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-ink-500/60">
        <div>
          <div className="label">With email</div>
          <div className="flex items-baseline gap-1.5 mt-0.5">
            <span className="font-mono text-[16px] font-semibold tabular-nums text-paper">
              {fmtInt(withEmail)}
            </span>
            <span className="font-mono text-[10px] text-paper-trace tabular-nums">
              {pct(withEmail)}
            </span>
          </div>
        </div>
        <div>
          <div className="label">No email</div>
          <div className="flex items-baseline gap-1.5 mt-0.5">
            <span className="font-mono text-[16px] font-semibold tabular-nums text-radar-violet">
              {fmtInt(noEmail)}
            </span>
            <span className="font-mono text-[10px] text-paper-trace tabular-nums">
              {pct(noEmail)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export function TitleDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data, isLoading } = useQuery({
    queryKey: ['title', slug],
    queryFn: () => api.get<TitleDetail>(`/api/titles/${slug}`),
    enabled: !!slug,
  })

  if (isLoading || !data) {
    return (
      <div className="p-8">
        <span className="label text-paper-trace animate-blink">Loading…</span>
      </div>
    )
  }

  const color = publicationColor(data.slug)
  const peakRevenue = Math.max(1, ...data.by_month.map((m) => m.total_revenue))

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/" className="label-amber hover:underline">← Overview</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">Title · {data.slug}</span>
      </div>

      <header className="border-b border-ink-500 pb-6">
        <div className="flex items-end justify-between">
          <div className="flex items-center gap-4">
            <PubBadge slug={data.slug} />
            <h1 className="font-serif text-[44px] leading-[1.05] tracking-tight text-paper">
              {data.name}
            </h1>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="label text-paper-faint">current rate</span>
            <span
              className="font-mono text-[22px] font-semibold tabular-nums"
              style={{ color }}
            >
              {fmtMoney(data.current_renewal_price, false, 2)}
            </span>
          </div>
        </div>
      </header>

      {/* Headline metrics */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Panel label="Active subscriptions" accent="amber">
          <Metric label="current" value={data.active_count} tone="amber" size="md" />
        </Panel>
        <Panel label="12-Month Revenue Forecast" accent="amber">
          {(() => {
            const t = data.totals_12mo
            const gross12 =
              t.auto_renew_revenue +
              t.manual_with_email_revenue +
              t.manual_no_email_revenue +
              t.agency_revenue_prevailing
            const remit12 = t.total_revenue
            return (
              <div className="space-y-3">
                <div>
                  <span className="label text-paper-faint">Gross · list</span>
                  <div className="font-mono text-[22px] font-semibold tabular-nums text-radar-green/75 leading-none mt-0.5">
                    {fmtMoney(gross12, true, 2)}
                  </div>
                </div>
                <div>
                  <span className="label text-paper-faint">Remit · cash</span>
                  <div className="font-mono text-[26px] font-semibold tabular-nums text-radar-green leading-none mt-0.5">
                    {fmtMoney(remit12, true, 2)}
                  </div>
                </div>
                <div className="pt-2 border-t border-ink-500/60">
                  <span className="label text-paper-faint">Subscriptions driving it</span>
                  <div className="font-mono text-[16px] font-semibold tabular-nums text-paper leading-none mt-0.5">
                    {fmtInt(t.total_count)}
                  </div>
                  <div className="label text-paper-trace mt-0.5">
                    period-end within 12 months
                  </div>
                </div>
              </div>
            )
          })()}
        </Panel>
        <Panel label="On auto-renew" accent="green">
          <SplitMetric
            total={data.auto_renew_count}
            totalTone="green"
            withEmail={data.auto_renew_with_email}
            noEmail={data.auto_renew_no_email}
          />
        </Panel>
        <Panel label="Manual renewal" accent="cyan">
          <SplitMetric
            total={data.manual_count}
            totalTone="cyan"
            withEmail={data.manual_with_email}
            noEmail={data.manual_no_email}
          />
        </Panel>
      </section>

      {/* 12-month schedule */}
      <section>
        <div className="label mb-3">
          Next 12 Months · Renewal Schedule (count / revenue at current rate)
        </div>
        <Panel accent="green" bodyClassName="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-500">
                  <th className="label py-2 px-4 font-normal text-left" rowSpan={2}>Month</th>
                  <th className="label py-2 px-4 font-normal text-center border-l border-ink-500 text-amber-glow" colSpan={1}>
                    Auto-Renew
                  </th>
                  <th className="label py-2 px-4 font-normal text-center border-l border-ink-500 text-radar-cyan" colSpan={2}>
                    Manual Expire
                  </th>
                  <th className="label py-2 px-4 font-normal text-center border-l border-ink-500 text-radar-red" colSpan={1}>
                    Agency
                  </th>
                  <th className="label py-2 px-4 font-normal text-center border-l border-ink-500 text-amber-glow" rowSpan={2}>
                    Total
                  </th>
                </tr>
                <tr className="border-b border-ink-500">
                  <th className="label py-2 px-4 font-normal text-right border-l border-ink-500 text-amber-glow">
                    <div>count</div>
                    <div className="text-paper-trace normal-case tracking-normal font-mono text-[9px]">est. revenue</div>
                  </th>
                  <th className="label py-2 px-4 font-normal text-right border-l border-ink-500 text-radar-cyan">
                    <div>w/ email</div>
                    <div className="text-paper-trace normal-case tracking-normal font-mono text-[9px]">potential rev.</div>
                  </th>
                  <th className="label py-2 px-4 font-normal text-right text-radar-violet">
                    <div>no email</div>
                    <div className="text-paper-trace normal-case tracking-normal font-mono text-[9px]">potential rev.</div>
                  </th>
                  <th className="label py-2 px-4 font-normal text-right border-l border-ink-500 text-radar-red">
                    <div>count</div>
                    <div className="text-paper-trace normal-case tracking-normal font-mono text-[9px]">@ list / remit</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.by_month.map((row) => {
                  const barW = (row.total_revenue / peakRevenue) * 100
                  return (
                    <tr
                      key={row.month}
                      className="border-b border-ink-500/40 hover:bg-ink-700/30 relative"
                    >
                      <td className="px-4 py-3 font-mono text-[12px] text-paper-dim uppercase tracking-label">
                        {row.month}
                      </td>
                      <RenewScheduleCell
                        count={row.auto_renew_count}
                        revenue={row.auto_renew_revenue}
                        tone="amber"
                      />
                      <RenewScheduleCell
                        count={row.manual_with_email_count}
                        revenue={row.manual_with_email_revenue}
                        tone="cyan"
                      />
                      <RenewScheduleCell
                        count={row.manual_no_email_count}
                        revenue={row.manual_no_email_revenue}
                        tone="violet"
                        noBorder
                      />
                      <AgencyScheduleCell
                        count={row.agency_count}
                        revenuePrevailing={row.agency_revenue_prevailing}
                        revenueRemit={row.agency_revenue_remit}
                      />
                      <td className="px-4 py-3 text-right border-l border-ink-500 relative">
                        <div
                          className={`font-mono font-semibold tabular-nums ${
                            row.total_count === 0 ? 'text-paper-trace' : 'text-paper'
                          }`}
                        >
                          {fmtInt(row.total_count)}
                        </div>
                        <div
                          className={`font-mono text-[11px] tabular-nums ${
                            row.total_revenue === 0 ? 'text-paper-trace' : 'text-radar-green'
                          }`}
                        >
                          {fmtMoney(row.total_revenue, false, 2)}
                        </div>
                        {row.total_revenue > 0 && (
                          <div
                            className="absolute bottom-0 right-0 h-[2px] bg-radar-green/40"
                            style={{ width: `${barW}%`, maxWidth: '100%' }}
                          />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="bg-ink-700/60 border-t-2 border-amber-glow/30">
                  <td className="px-4 py-3">
                    <span className="label-amber">12-MO TOTAL</span>
                  </td>
                  <RenewScheduleCell
                    count={data.totals_12mo.auto_renew_count}
                    revenue={data.totals_12mo.auto_renew_revenue}
                    tone="amber"
                    bold
                  />
                  <RenewScheduleCell
                    count={data.totals_12mo.manual_with_email_count}
                    revenue={data.totals_12mo.manual_with_email_revenue}
                    tone="cyan"
                    bold
                  />
                  <RenewScheduleCell
                    count={data.totals_12mo.manual_no_email_count}
                    revenue={data.totals_12mo.manual_no_email_revenue}
                    tone="violet"
                    noBorder
                    bold
                  />
                  <AgencyScheduleCell
                    count={data.totals_12mo.agency_count}
                    revenuePrevailing={data.totals_12mo.agency_revenue_prevailing}
                    revenueRemit={data.totals_12mo.agency_revenue_remit}
                    bold
                  />
                  <td className="px-4 py-3 text-right border-l border-ink-500">
                    <div className="font-mono font-semibold tabular-nums text-amber-glow">
                      {fmtInt(data.totals_12mo.total_count)}
                    </div>
                    <div className="font-mono font-semibold text-[13px] tabular-nums text-radar-green">
                      {fmtMoney(data.totals_12mo.total_revenue, false, 2)}
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Panel>

        {data.out_of_window_count > 0 && (
          <div className="mt-3 text-right">
            <span className="label text-paper-dim">
              {fmtInt(data.out_of_window_count)} active subs with period-end beyond 12 months
              (multi-year / lifetime)
            </span>
          </div>
        )}
      </section>
    </div>
  )
}
